import React, { createContext, useCallback, useEffect, useMemo, useReducer, useRef } from 'react'
import {
  artifactManagerService,
  getUserAlbums,
  getUserMemories,
  getAlbumMemories,
  MemoriesProvider,
} from '@fs/zion-memories/memories-authorized-use-only'
import { useContainerWidth } from '@fs/zion-ui'
import { useEffectiveUser } from '@fs/zion-user'
import { useFeatureFlag } from '@fs/zion-flags'
import { useHistory } from '@fs/zion-router'

const GalleryContext = createContext()
const GalleryApiContext = createContext()

const galleryUrls = {
  gallery: '/gallery',
  archive: '/gallery/my-archive',
  recentlyDeleted: '/gallery/recently-deleted',
  bookmarks: '/gallery/bookmarks',
  album: '/gallery/album',
}

const galleryReducerTypes = {
  init: 'INIT',
  setAlbums: 'setAlbums',
  setDisplayType: 'SET_DISPLAY_TYPE',
  setMemoriesErrors: 'SET_MEMORIES_ERRORS',
  setMemoriesLoading: 'SET_MEMORIES_LOADING',
  setSelectedAlbumId: 'SET_SELECTED_ALBUM_ID',
  setSelectMode: 'SET_SELECT_MODE',
  setShowExplorer: 'SET_SHOW_EXPLORER',
  setMemoryFilters: 'SET_MEMORY_FILTERS',
  setSelectedIds: 'SET_SELECTED_IDS',
  setSortingType: 'SET_SORTING_FUNCTION',
  setSortingControl: 'SET_SORTING_CONTROL',
  setActivePage: 'SET_ACTIVE_PAGE',
  setAlbumMemories: 'SET_ALBUM_MEMORIES',
}

const galleryItems = {
  albums: 'albums',
  archive: 'archive',
  bookmarks: 'bookmarks',
  deleted: 'deleted',
  memories: 'memories',
  missing: 'missing',
}

const displayTypes = {
  grid: 'grid',
  list: 'list',
}

const sortingTypes = {
  date: 'date',
  dateEdited: 'dateEdited',
  title: 'title',
  type: 'type',
}

const sortingDirection = {
  ascending: 'ascending',
  descending: 'descending',
}

const memoryFilterOptions = {
  audios: true,
  documents: true,
  photos: true,
  stories: true,
  private: true,
  public: true,
  location: true,
  date: true,
  person: true,
  tag: true,
  inAlbum: true,
}

const galleryReducer = (state, action) => {
  switch (action.type) {
    case galleryReducerTypes.init:
      return {
        ...state,
        albums: action.albums,
        bookmarkedAlbums: action.bookmarkedAlbums,
        archivedMemories: action.archivedMemories,
        bookmarkedMemories: action.bookmarkedMemories,
        missingMemories: action.missingMemories,
        recentlyDeletedMemories: action.recentlyDeletedMemories,
        userMemories: action.userMemories,
      }
    case galleryReducerTypes.setAlbums:
      return { ...state, albums: action.albums }
    case galleryReducerTypes.setShowExplorer:
      return { ...state, showExplorer: action.showExplorer }
    case galleryReducerTypes.setSelectedAlbumId:
      return { ...state, selectedAlbumId: action.selectedAlbumId }
    case galleryReducerTypes.setMemoriesLoading:
      return { ...state, memoriesLoading: action.memoriesLoading }
    case galleryReducerTypes.setMemoriesErrors:
      return { ...state, memoriesErrors: [...state.memoriesErrors, ...action.memoriesErrors] }
    case galleryReducerTypes.setDisplayType:
      return { ...state, displayType: action.displayType }
    case galleryReducerTypes.setSelectMode:
      return { ...state, selectMode: action.selectMode }
    case galleryReducerTypes.setMemoryFilters:
      return { ...state, memoryFilters: action.memoryFilters }
    case galleryReducerTypes.setSelectedIds:
      return {
        ...state,
        selectedIds: action.selectedIds,
      }
    case galleryReducerTypes.setSortingType:
      return { ...state, sortingType: action.sortingType }
    case galleryReducerTypes.setSortingControl:
      return { ...state, sortingControl: action.sortingControl }
    case galleryReducerTypes.setActivePage:
      return { ...state, activePage: action.activePage }
    case galleryReducerTypes.setAlbumMemories:
      return {
        ...state,
        albumsLoading: false,
        albumMemoriesMap: { ...state.albumMemoriesMap, ...action.albumMemoriesMap },
        albumErrorsMap: { ...state.albumErrorsMap, ...action.albumErrorsMap },
        memoriesInAlbums: { ...state.memoriesInAlbums, ...action.memoriesInAlbums },
      }
    default:
      return state
  }
}

const GalleryProvider = ({ children }) => {
  const isCurrent = useRef(true)
  const history = useHistory()
  const { cisId } = useEffectiveUser()
  const atSize = useContainerWidth()
  const { isOn: enableRestoreDashboardFlag } = useFeatureFlag('memories_enableRestoreDashboard')
  const isMobile = atSize({ default: true, xl: false })
  const { getAllMissingMemories, getRecentlyDeletedMemories } = artifactManagerService

  const initialState = useMemo(
    () => ({
      activePage: galleryUrls.gallery,
      albumMemoriesMap: {},
      albumErrorsMap: {},
      albumsLoading: true,
      showExplorer: !isMobile,
      memoriesLoading: false,
      memoriesErrors: [],
      albums: [],
      bookmarkedAlbums: [],
      archivedMemories: [],
      bookmarkedMemories: [],
      recentlyDeletedMemories: [],
      userMemories: [],
      missingMemories: [],
      selectedAlbumId: null,
      displayType: displayTypes.grid,
      selectMode: false,
      memoryFilters: memoryFilterOptions,
      memoriesInAlbums: [],
      selectedIds: [],
      sortingType: sortingTypes.date,
      sortingControl: sortingDirection.ascending,
    }),
    [isMobile]
  )
  const [state, dispatch] = useReducer(galleryReducer, initialState)

  const galleryActions = useMemo(
    () => ({
      init: ({
        archivedMemories,
        bookmarkedMemories,
        recentlyDeletedMemories,
        userMemories,
        missingMemories,
        albums,
        bookmarkedAlbums,
      }) =>
        dispatch({
          type: galleryReducerTypes.init,
          archivedMemories,
          bookmarkedMemories,
          recentlyDeletedMemories,
          userMemories,
          missingMemories,
          albums,
          bookmarkedAlbums,
        }),
      setAlbums: ({ albums, albumsError }) => {
        dispatch({ type: galleryReducerTypes.setAlbums, albums })
        if (albumsError?.length > 0) {
          dispatch({ type: galleryReducerTypes.setMemoriesErrors, memoriesErrors: albumsError })
        }
      },
      setShowExplorer: (showExplorer) => dispatch({ type: galleryReducerTypes.setShowExplorer, showExplorer }),
      setSelectedAlbumId: (selectedAlbumId) =>
        dispatch({
          type: galleryReducerTypes.setSelectedAlbumId,
          selectedAlbumId,
        }),
      setMemoriesErrors: (memoriesErrors) =>
        dispatch({
          type: galleryReducerTypes.setMemoriesErrors,
          memoriesErrors,
        }),
      setMemoriesLoading: (memoriesLoading) =>
        dispatch({
          type: galleryReducerTypes.setMemoriesLoading,
          memoriesLoading,
        }),
      setActivePage: (activePage) => dispatch({ type: galleryReducerTypes.setActivePage, activePage }),
      setAlbumMemories: ({ albumMemoriesMap, albumErrorsMap, memoriesInAlbums }) =>
        dispatch({ type: galleryReducerTypes.setAlbumMemories, albumMemoriesMap, albumErrorsMap, memoriesInAlbums }),
      setDisplayType: (displayType) => dispatch({ type: galleryReducerTypes.setDisplayType, displayType }),
      setSelectMode: (selectMode) => dispatch({ type: galleryReducerTypes.setSelectMode, selectMode }),
      setMemoryFilters: (memoryFilters) => dispatch({ type: galleryReducerTypes.setMemoryFilters, memoryFilters }),
      setSelectedIds: (selectedIds) =>
        dispatch({
          type: galleryReducerTypes.setSelectedIds,
          selectedIds,
        }),
      setSortingType: (sortingType) => dispatch({ type: galleryReducerTypes.setSortingType, sortingType }),
      setSortingControl: (sortingControl) => dispatch({ type: galleryReducerTypes.setSortingControl, sortingControl }),
    }),
    []
  )

  const fetchMemories = useCallback(async (apiParams) => {
    let memories = []
    let memoryError = null
    try {
      memories = (await getUserMemories(apiParams)) ?? []
    } catch (error) {
      memoryError = galleryItems.archive
    }
    return { memories, memoryError }
  }, [])

  const fetchRecentlyDeletedMemories = useCallback(async () => {
    let memories = []
    let memoryError = null
    try {
      memories =
        (await getRecentlyDeletedMemories({ cisId }))?.data?.tombstones?.map((memory) => memory?.artifactList?.[0]) ??
        []
    } catch (error) {
      memoryError = galleryItems.deleted
    }
    return { memories, memoryError }
  }, [cisId, getRecentlyDeletedMemories])

  const fetchMissingMemories = useCallback(async () => {
    let memories = []
    let memoryError = null
    if (enableRestoreDashboardFlag) {
      try {
        memories = (await getAllMissingMemories())?.data?.missing ?? []
      } catch (error) {
        memoryError = galleryItems.missing
      }
    } else {
      memories = []
    }
    return { memories, memoryError }
  }, [enableRestoreDashboardFlag, getAllMissingMemories])

  const fetchAlbums = useCallback(async (apiParams) => {
    let albums = []
    let albumsError = null
    try {
      albums = (await getUserAlbums(apiParams)) ?? []
    } catch (error) {
      albumsError = galleryItems.albums
    }
    return { albums, albumsError }
  }, [])

  useEffect(() => {
    const loadUserMemories = async () => {
      if (cisId) {
        galleryActions.setMemoriesLoading(true)

        const { memories: archivedMemories, memoryError: archivedError } = await fetchMemories({
          cisId,
          archived: true,
        })
        const { memories: bookmarkedMemories, memoryError: bookmarkedError } = await fetchMemories({
          cisId,
          bookmarked: true,
        })
        const { memories: userMemories, memoryError: userMemoryError } = await fetchMemories({ cisId })
        const { memories: missingMemories, memoryError: missingError } = await fetchMissingMemories()
        const {
          memories: recentlyDeletedMemories,
          memoryError: recentlyDeletedError,
        } = await fetchRecentlyDeletedMemories()
        const { albums, albumsError } = await fetchAlbums({ cisId })
        const { albums: bookmarkedAlbums, albumsError: bookmarkedAlbumsError } = await fetchAlbums({
          cisId,
          bookmarked: true,
        })

        const memoriesErrors = []
        if (archivedError) memoriesErrors.push(galleryItems.archive)
        if (bookmarkedError) memoriesErrors.push(galleryItems.bookmarks)
        if (missingError) memoriesErrors.push(galleryItems.missing)
        if (recentlyDeletedError) memoriesErrors.push(galleryItems.deleted)
        if (userMemoryError) memoriesErrors.push(galleryItems.memories)
        if (albumsError) memoriesErrors.push(galleryItems.albums)
        if (bookmarkedAlbumsError) memoriesErrors.push(galleryItems.bookmarks)

        if (isCurrent.current) {
          galleryActions.init({
            archivedMemories,
            bookmarkedMemories,
            recentlyDeletedMemories,
            userMemories,
            missingMemories,
            albums,
            bookmarkedAlbums,
          })

          galleryActions.setMemoriesLoading(false)

          if (memoriesErrors?.length) {
            galleryActions.setMemoriesErrors(memoriesErrors)
          }
        }
      }
    }
    loadUserMemories()
  }, [
    cisId,
    fetchAlbums,
    fetchMemories,
    fetchMissingMemories,
    fetchRecentlyDeletedMemories,
    galleryActions,
    getAllMissingMemories,
    getRecentlyDeletedMemories,
  ])

  // If the user shrinks their browser we want the explorer to be initially closed so it doesn't cover things up
  useEffect(() => {
    if (isMobile) {
      galleryActions.setShowExplorer(false)
    }
  }, [galleryActions, isMobile])

  /**
   * This sets the initial active page appropriately if the user goes directly to a url in the gallery.
   * This is why we only need the useEffect to run on the initial page load.
   */
  useEffect(() => {
    let activePage = ''
    const pathname = history.location.pathname
    if (pathname) {
      if (pathname.includes(galleryUrls.album)) {
        activePage = pathname.slice(0, pathname.lastIndexOf('/'))
      } else {
        activePage = pathname
      }
      galleryActions.setActivePage(activePage)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // This will get the album id from the url when the page first loads or a user goes straight to an album's page
  // It prevents it from updating state because the state is otherwise updated when the list item is clicked
  useEffect(() => {
    const getMemories = async (id) => {
      let albumError = false
      let albumMemories = []
      try {
        albumMemories = await getAlbumMemories(id)
      } catch (error) {
        albumError = true
      }
      const albumErrorsMap = { [id]: albumError }
      const albumMemoriesMap = { [id]: albumMemories }
      const memoriesInAlbums = albumMemories
      isCurrent.current && galleryActions.setAlbumMemories({ albumMemoriesMap, albumErrorsMap, memoriesInAlbums })
    }

    const pathname = history.location.pathname
    if (pathname.startsWith(galleryUrls.album)) {
      const id = pathname.slice(pathname.lastIndexOf('/') + 1)
      if (state.selectedAlbumId !== id) {
        isCurrent.current && galleryActions.setSelectedAlbumId(id)
        getMemories(id)
      }
    }
  }, [galleryActions, history.location.pathname, state])

  useEffect(() => {
    isCurrent.current = true
    return () => {
      isCurrent.current = false
    }
  }, [])

  useEffect(() => {
    const getAlbums = async () => {
      const albumsToProcess = [...state.albums, ...state.bookmarkedAlbums]
      const memoriesPromises = albumsToProcess.map(async (album) => getAlbumMemories(album.id))
      const memoriesResponses = await Promise.allSettled(memoriesPromises)
      const albumMemoriesMap = albumsToProcess.reduce(
        (map, album, index) => ({ ...map, [album.id]: memoriesResponses[index]?.value }),
        {}
      )
      const albumErrorsMap = albumsToProcess.reduce(
        (map, album, index) => ({ ...map, [album.id]: memoriesResponses[index]?.status === 'rejected' }),
        {}
      )
      // Flattens the array of arrays into a single array for use in filtering memories that are in albums.
      const memoriesInAlbums = memoriesResponses?.map((memories) => memories?.value).flat()
      isCurrent.current && galleryActions.setAlbumMemories({ albumMemoriesMap, albumErrorsMap, memoriesInAlbums })
    }
    if (state.albums.length) {
      getAlbums()
    }
  }, [galleryActions, state.albums, state.bookmarkedAlbums])

  const apiValues = useMemo(() => ({ galleryActions }), [galleryActions])

  const {
    activePage,
    albumMemoriesMap,
    albumErrorsMap,
    albumsLoading,
    albums,
    archivedMemories,
    bookmarkedAlbums,
    bookmarkedMemories,
    displayType,
    memoriesErrors,
    memoriesLoading,
    missingMemories,
    recentlyDeletedMemories,
    selectedAlbumId,
    selectMode,
    showExplorer,
    userMemories,
    memoryFilters,
    memoriesInAlbums,
    selectedIds,
    sortingType,
    sortingControl,
  } = state

  const selectedIdsCount = useMemo(() => selectedIds?.length ?? 0, [selectedIds])

  const activeMemories = useMemo(() => {
    let memories = []
    switch (activePage) {
      case galleryUrls.gallery:
        memories = userMemories
        break
      case galleryUrls.archive:
        memories = archivedMemories
        break
      case galleryUrls.recentlyDeleted:
        memories = recentlyDeletedMemories
        break
      case galleryUrls.bookmarks:
        memories = bookmarkedMemories
        break
      case galleryUrls.album:
        memories = albumMemoriesMap[selectedAlbumId]
        break
      default:
    }
    return memories
  }, [
    activePage,
    albumMemoriesMap,
    archivedMemories,
    bookmarkedMemories,
    recentlyDeletedMemories,
    selectedAlbumId,
    userMemories,
  ])

  const dataValues = useMemo(
    () => ({
      activeMemories,
      activePage,
      albumErrorsMap,
      albumsLoading,
      albumMemoriesMap,
      albums,
      archivedMemories,
      bookmarkedAlbums,
      bookmarkedMemories,
      displayType,
      isMobile,
      memoriesErrors,
      memoriesLoading,
      missingMemories,
      recentlyDeletedMemories,
      selectedAlbumId,
      selectMode,
      showExplorer,
      userMemories,
      memoryFilters,
      memoriesInAlbums,
      selectedIds,
      selectedIdsCount,
      sortingType,
      sortingControl,
    }),
    [
      activeMemories,
      activePage,
      albumErrorsMap,
      albumsLoading,
      albumMemoriesMap,
      albums,
      archivedMemories,
      bookmarkedAlbums,
      bookmarkedMemories,
      displayType,
      isMobile,
      memoriesErrors,
      memoriesLoading,
      missingMemories,
      recentlyDeletedMemories,
      selectedAlbumId,
      selectMode,
      showExplorer,
      userMemories,
      memoryFilters,
      memoriesInAlbums,
      selectedIds,
      selectedIdsCount,
      sortingType,
      sortingControl,
    ]
  )

  const loading = useMemo(() => albumsLoading || memoriesLoading, [albumsLoading, memoriesLoading])
  const memories = useMemo(() => activeMemories ?? [], [activeMemories])

  return (
    <GalleryContext.Provider value={dataValues}>
      <GalleryApiContext.Provider value={apiValues}>
        <MemoriesProvider memories={memories} memoriesLoading={loading}>
          {children}
        </MemoriesProvider>
      </GalleryApiContext.Provider>
    </GalleryContext.Provider>
  )
}

export {
  displayTypes,
  galleryItems,
  galleryReducerTypes,
  galleryUrls,
  GalleryApiContext,
  GalleryContext,
  GalleryProvider,
  memoryFilterOptions,
  sortingTypes,
  sortingDirection,
}
